import React from "react";
import { partial } from "lodash";
import PropTypes from "prop-types";
import { Redirect } from "react-router-dom";

import {
  ADMIN_ROLE,
  ALLOWED_ROLES,
  hasAdminRole,
  isInternalSupportUser,
  isInternalTestUser,
} from "src/services/auth";
import { withAccess } from "src/components/Can";
import { isLocalEnvironment } from "src/services/localSettings";
import { isOrgDesignBetaEnabled } from "src/components/OrgChart/controller/dnd/featureFlags";

import PATHS, { PERSON_TAB, url } from "../../consts/urlPaths";
import { BAMBOOHR_OAUTH_CALLBACK_PATH } from "src/integrationTypes/components/bambooHROAuth";

import {
  CreateWorkspacePage,
  InternalDashboard,
  WorkspacesPage,
  WorkspaceConfigurationPage,
  AdminConfiguration,
  WorkspaceSettings,
  OrgDesignSettingsPage,
} from "../adminPages";

import { PAGE_TITLE_CONFIGS } from "../Helmet";
import {
  loadAIDashboardLibrary,
  loadAIDashboards,
  loadAIDashboardsV2,
  loadApiDocs,
  loadAuth0UserReport,
  loadBambooHROAuthCallbackPage,
  loadDesignDetailPage,
  loadDesignEditPage,
  loadDesignsHub,
  loadExplorePage,
  loadGoalsExplorer,
  loadGuardrailAdminPage,
  loadGuardrailsHubPage,
  loadHomePageContainer,
  loadInsightsPage,
  loadLoginHistoryPage,
  loadLogout,
  loadPeopleSearchRedirect,
  loadPersonDetailLinkPage,
  loadPersonDetailPage,
  loadPositionDetailPage,
  loadSearchPage,
  loadSlideDeckPage,
  loadSlideDecksPage,
  loadTagDetailsPage,
  loadTagsExplore,
  loadTeamCanvasPage,
  loadTeamDetailPage,
  loadTeamListPage,
  loadTeamsExplorer,
  loadWorkflowBuilderPage,
  loadWorkflowsPage,
  loadWorkforceInboxPage,
  loadWorkforcePage,
  loadWorkforcePlanningDashboard,
} from "../lazyRouteLoaders.js";

const ExplorePage = React.lazy(loadExplorePage);
const TagDetailsPage = React.lazy(loadTagDetailsPage);
const PersonDetailPage = React.lazy(loadPersonDetailPage);
const PositionDetailPage = React.lazy(loadPositionDetailPage);
const PersonDetailLinkPage = React.lazy(loadPersonDetailLinkPage);
const TeamDetailPage = React.lazy(loadTeamDetailPage);
const AIDashboards = React.lazy(loadAIDashboards);
const AIDashboardsV2 = React.lazy(loadAIDashboardsV2);
const AIDashboardLibrary = React.lazy(loadAIDashboardLibrary);
const TeamCanvasPage = React.lazy(loadTeamCanvasPage);
const SlideDecksPage = React.lazy(loadSlideDecksPage);
const SlideDeckPage = React.lazy(loadSlideDeckPage);
const InsightsPage = React.lazy(loadInsightsPage);
const PeopleSearchRedirect = React.lazy(loadPeopleSearchRedirect);
const WorkforcePage = React.lazy(loadWorkforcePage);
const TeamListPage = React.lazy(loadTeamListPage);
const SearchPage = React.lazy(loadSearchPage);
const WorkforcePlanningDashboard = React.lazy(loadWorkforcePlanningDashboard);
const GuardrailsHubPage = React.lazy(loadGuardrailsHubPage);
const WorkforceInboxPage = React.lazy(loadWorkforceInboxPage);
const GuardrailAdminPage = React.lazy(loadGuardrailAdminPage);
const TagsExplore = React.lazy(loadTagsExplore);
const TeamsExplorer = React.lazy(loadTeamsExplorer);
const GoalsExplorer = React.lazy(loadGoalsExplorer);
const Logout = React.lazy(loadLogout);
const ApiDocs = React.lazy(loadApiDocs);
const LoginHistoryPage = React.lazy(loadLoginHistoryPage);
const Auth0UserReport = React.lazy(loadAuth0UserReport);
const WorkflowsPage = React.lazy(loadWorkflowsPage);
const WorkflowBuilderPage = React.lazy(loadWorkflowBuilderPage);
const DesignsHub = React.lazy(loadDesignsHub);
const DesignDetailPage = React.lazy(loadDesignDetailPage);
const DesignEditPage = React.lazy(loadDesignEditPage);
const BambooHROAuthCallbackPage = React.lazy(loadBambooHROAuthCallbackPage);
const HomePageContainer = React.lazy(loadHomePageContainer);

/** Lazy chunk loaders keyed by route path (single source with React.lazy above). */
const PREFETCH_LOADER_BY_PATH = {
  [PATHS.SEARCH]: loadSearchPage,
  [PATHS.TAG_EXPLORE]: loadTagsExplore,
  [PATHS.TEAM_EXPLORE]: loadTeamsExplorer,
  [PATHS.GOALS_EXPLORE]: loadGoalsExplorer,
  [PATHS.TAG_DETAILS]: loadTagDetailsPage,
  [PATHS.TEAM_DETAILS]: loadTeamDetailPage,
  [PATHS.LOGOUT]: loadLogout,
  [PATHS.API_DOCS]: loadApiDocs,
  [PATHS.LOGIN_HISTORY_PAGE]: loadLoginHistoryPage,
  [PATHS.POSITION_DETAILS]: loadPositionDetailPage,
  [PATHS.POSITION_DETAILS_BASE_PATH]: loadPositionDetailPage,
  [PATHS.PERSON_DETAILS]: loadPersonDetailPage,
  [PATHS.PERSON_DETAILS_EMAIL_LINK]: loadPersonDetailLinkPage,
  [PATHS.EXPLORE]: loadExplorePage,
  [PATHS.INSIGHTS]: loadInsightsPage,
  [PATHS.AI_DASHBOARDS_LIBRARY]: loadAIDashboardLibrary,
  [PATHS.AI_DASHBOARDS_V2]: loadAIDashboardsV2,
  [PATHS.AI_DASHBOARDS]: loadAIDashboards,
  [PATHS.TEAM_CANVAS]: loadTeamCanvasPage,
  [PATHS.SLIDES]: loadSlideDecksPage,
  [PATHS.SLIDE_DECK]: loadSlideDeckPage,
  [PATHS.WORKFORCE]: loadWorkforcePage,
  [PATHS.TEAMS]: loadTeamListPage,
  [PATHS.PEOPLE]: loadPeopleSearchRedirect,
  [PATHS.USER_HOME]: loadPersonDetailPage,
  [PATHS.ORG_DESIGN_HOME]: loadHomePageContainer,
  [PATHS.WORKFORCE_PLANNING_DASHBOARD]: loadWorkforcePlanningDashboard,
  [PATHS.WORKFORCE_PLANNING_GUARDRAILS_COMMAND_TOWER]: loadGuardrailsHubPage,
  [PATHS.WORKFORCE_PLANNING_GUARDRAILS]: loadGuardrailsHubPage,
  [PATHS.WORKFORCE_INBOX]: loadWorkforceInboxPage,
  [PATHS.WORKFORCE_PLANNING_GUARDRAILS_ADMIN]: loadGuardrailAdminPage,
  [PATHS.WORKFLOWS]: loadWorkflowsPage,
  [PATHS.WORKFLOW_BUILDER]: loadWorkflowBuilderPage,
  [PATHS.DESIGNS]: loadDesignsHub,
  [PATHS.DESIGN_EDIT]: loadDesignEditPage,
  [PATHS.DESIGN_DETAIL]: loadDesignDetailPage,
  [PATHS.DIRECTORY_USER_HOME]: loadPersonDetailPage,
  [PATHS.DIRECTORY_USER_HOME_BASE_PATH]: loadPersonDetailPage,
  [BAMBOOHR_OAUTH_CALLBACK_PATH]: loadBambooHROAuthCallbackPage,
  [PATHS.USER_REPORT]: loadAuth0UserReport,
};

function attachPrefetchLoaders(routes) {
  return routes.map((route) => {
    const prefetchLoader = PREFETCH_LOADER_BY_PATH[route.path];
    return prefetchLoader ? { ...route, prefetchLoader } : route;
  });
}

/**
 * Directory User Router Component
 * Renders PersonDetailPage and handles redirect to dashboard tab if no tabType specified
 * @param {Object} routeProps - Route props passed from React Router
 * @returns {React.Component} PersonDetailPage with ExplorePanel
 */
const DirectoryUserRouter = (routeProps) => {
  const { match } = routeProps;
  const hasTabType = match?.params?.tabType;

  // If we're at /home without a tabType, redirect to /home/dashboard
  if (!hasTabType) {
    return (
      <Redirect
        to={url(PATHS.DIRECTORY_USER_HOME, {
          tabType: PERSON_TAB.USER_DASHBOARD,
        })}
      />
    );
  }
  // Show the PersonDetailPage interface
  return <PersonDetailPage {...routeProps} />;
};

DirectoryUserRouter.propTypes = {
  // All props are forwarded to PersonDetailPage
  match: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
};

const getRoutes = (
  props,
  userPersonAggregateId,
  disablePersonalisation,
  enableDesigns = false,
  orgConfigLoading = false,
  enableDemoODOptions = false,
  workspaceFlagsLoading = false,
  enableAIChat = false,
  enableTeamCanvas = false,
  enableOrgDesign = false,
  enablePositionBackedVacancies = false,
  enableAIDataDrivenDashboards = false,
  enableSlides = false,
  enableWorkforceGuardrails = false,
  enableInbox = false
) => {
  const { user } = props;

  const withAdminAccess = partial(withAccess, user, [ADMIN_ROLE]);
  const withDesignsAccess = partial(withAccess, user, ALLOWED_ROLES.DESIGNS);
  const noUserId = !userPersonAggregateId;
  const showDevTools =
    isInternalSupportUser(user) ||
    isInternalTestUser(user) ||
    isLocalEnvironment();
  // withAdminAccess cannot carry this: canPerform short-circuits true for
  // support users, so the admin requirement has to be checked here.
  const showWorkforce =
    hasAdminRole(user) &&
    isInternalSupportUser(user) &&
    !!enablePositionBackedVacancies;

  return attachPrefetchLoaders([
    {
      exact: true,
      title: "Search",
      path: PATHS.SEARCH,
      Component: SearchPage,
    },
    {
      path: PATHS.TAG_EXPLORE,
      Component: TagsExplore,
    },
    {
      path: PATHS.TEAM_EXPLORE,
      Component: TeamsExplorer,
      title: "Teams Explorer",
    },
    {
      path: PATHS.GOALS_EXPLORE,
      Component: GoalsExplorer,
      title: "Goals Explorer",
    },
    {
      title: PAGE_TITLE_CONFIGS.tagDetailPage,
      path: PATHS.TAG_DETAILS,
      Component: TagDetailsPage,
    },
    {
      title: PAGE_TITLE_CONFIGS.teamDetailPage,
      path: PATHS.TEAM_DETAILS,
      Component: TeamDetailPage,
    },
    { exact: true, path: PATHS.LOGOUT, Component: Logout },
    {
      exact: true,
      title: "Api documentation",
      path: PATHS.API_DOCS,
      Component: ApiDocs,
    },
    {
      exact: true,
      title: "Login History",
      path: PATHS.LOGIN_HISTORY_PAGE,
      Component: LoginHistoryPage,
    },
    {
      exact: true,
      title: PAGE_TITLE_CONFIGS.positionDetailPage,
      path: PATHS.POSITION_DETAILS,
      Component: PositionDetailPage,
    },
    {
      exact: true,
      title: PAGE_TITLE_CONFIGS.positionDetailPage,
      path: PATHS.POSITION_DETAILS_BASE_PATH,
      Component: PositionDetailPage,
    },
    {
      exact: true,
      title: PAGE_TITLE_CONFIGS.personDetailPage,
      path: PATHS.PERSON_DETAILS,
      Component: PersonDetailPage,
    },
    {
      exact: true,
      title: "Redirecting",
      path: PATHS.PERSON_DETAILS_EMAIL_LINK,
      Component: PersonDetailLinkPage,
    },
    {
      exact: true,
      title: "Explore",
      path: PATHS.EXPLORE,
      Component: ExplorePage,
    },
    // Restricted routes
    {
      exact: true,
      isRestricted: true,
      title: "Insights",
      path: PATHS.INSIGHTS,
      Component: InsightsPage,
    },
    // Any signed-in role can open a dashboard they generated. The API only
    // returns a row when the chat participant is the caller, and hydration
    // still runs each query with that caller's roles.
    {
      exact: true,
      isDisabled: !enableAIChat,
      title: "Dashboard Library",
      path: PATHS.AI_DASHBOARDS_LIBRARY,
      Component: AIDashboardLibrary,
    },
    {
      exact: true,
      // Data-driven canvas — workspace flag enableAIDataDrivenDashboards.
      isDisabled: !enableAIChat || !enableAIDataDrivenDashboards,
      title: "Dashboards",
      path: PATHS.AI_DASHBOARDS_V2,
      Component: AIDashboardsV2,
    },
    {
      exact: false,
      // AI dashboards route — gated on enableAIChat.
      isDisabled: !enableAIChat,
      title: "Dashboards",
      path: PATHS.AI_DASHBOARDS,
      Component: AIDashboards,
    },
    {
      exact: true,
      isRestricted: true,
      // Beta: release + OD beta + enableTeamCanvas.
      // Loading grace so deep links stay alive while flags resolve.
      isDisabled:
        !(
          isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
          enableTeamCanvas
        ) && !workspaceFlagsLoading,
      title: "Team Canvas",
      path: PATHS.TEAM_CANVAS,
      Component: TeamCanvasPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableSlides && !workspaceFlagsLoading,
      title: "Slides",
      path: PATHS.SLIDES,
      Component: SlideDecksPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableSlides && !workspaceFlagsLoading,
      title: "Slides",
      path: PATHS.SLIDE_DECK,
      Component: SlideDeckPage,
    },
    {
      exact: true,
      isRestricted: true,
      // Before PEOPLE so /workforce/people is not parsed as workspaceId=workforce.
      isDisabled: !showWorkforce && !workspaceFlagsLoading,
      title: "Workforce",
      path: PATHS.WORKFORCE,
      Component: WorkforcePage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Teams",
      path: PATHS.TEAMS,
      Component: TeamListPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "People",
      path: PATHS.PEOPLE,
      Component: PeopleSearchRedirect,
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      isRestricted: true,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.USER_HOME,
      Component: PersonDetailPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled:
        !(
          noUserId &&
          isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions })
        ) && !workspaceFlagsLoading,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.ORG_DESIGN_HOME,
      Component: HomePageContainer,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Configuration",
      path: PATHS.DATASOURCE,
      Component: withAdminAccess(WorkspaceConfigurationPage, props),
    },
    {
      exact: true,
      title: "Workforce planning dashboard",
      path: PATHS.WORKFORCE_PLANNING_DASHBOARD,
      Component: WorkforcePlanningDashboard,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableWorkforceGuardrails && !workspaceFlagsLoading,
      title: "Guardrails",
      path: PATHS.WORKFORCE_PLANNING_GUARDRAILS_COMMAND_TOWER,
      Component: GuardrailsHubPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableWorkforceGuardrails && !workspaceFlagsLoading,
      title: "Guardrails",
      path: PATHS.WORKFORCE_PLANNING_GUARDRAILS,
      Component: GuardrailsHubPage,
    },
    {
      exact: true,
      isRestricted: true,
      isDisabled: !enableInbox && !workspaceFlagsLoading,
      title: "Inbox",
      path: PATHS.WORKFORCE_INBOX,
      Component: WorkforceInboxPage,
    },
    {
      exact: true,
      isRestricted: true,
      allowedRoles: [ADMIN_ROLE],
      isDisabled: !enableWorkforceGuardrails && !workspaceFlagsLoading,
      title: "Guardrail rules",
      path: PATHS.WORKFORCE_PLANNING_GUARDRAILS_ADMIN,
      Component: GuardrailAdminPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Configuration",
      path: PATHS.WORKSPACE_CONFIGURATION,
      Component: withAdminAccess(WorkspaceConfigurationPage, props),
    },

    {
      exact: true,
      isRestricted: true,
      title: "New workspace",
      path: PATHS.CREATE_WORKSPACE,
      Component: withAdminAccess(CreateWorkspacePage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workspaces",
      path: PATHS.WORKSPACES,
      Component: withAdminAccess(WorkspacesPage, props),
    },
    // Workflow routes
    {
      exact: true,
      isRestricted: true,
      title: "Workflows",
      path: PATHS.WORKFLOWS,
      Component: withAdminAccess(WorkflowsPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workflow Builder",
      path: PATHS.WORKFLOW_BUILDER,
      Component: withAdminAccess(WorkflowBuilderPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      allowedRoles: ALLOWED_ROLES.DESIGNS,
      // Keep routes mounted while org config loads so deep links / refresh work
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Designs",
      path: PATHS.DESIGNS,
      Component: withDesignsAccess(DesignsHub, props),
    },
    {
      exact: true,
      isRestricted: true,
      allowedRoles: ALLOWED_ROLES.DESIGNS,
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Edit design",
      path: PATHS.DESIGN_EDIT,
      Component: withDesignsAccess(DesignEditPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      allowedRoles: ALLOWED_ROLES.DESIGNS,
      isDisabled: !enableDesigns && !orgConfigLoading,
      title: "Design",
      path: PATHS.DESIGN_DETAIL,
      Component: withDesignsAccess(DesignDetailPage, props),
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.DIRECTORY_USER_HOME,
      Component: DirectoryUserRouter,
    },
    {
      exact: true,
      isDisabled: disablePersonalisation || noUserId,
      title: PAGE_TITLE_CONFIGS.dashboard,
      path: PATHS.DIRECTORY_USER_HOME_BASE_PATH,
      Component: DirectoryUserRouter,
    },
    {
      exact: true,
      isDisabled: !showDevTools,
      isRestricted: true,
      title: "Internal dashboard",
      path: PATHS.INTERNAL_DASHBOARD,
      Component: InternalDashboard,
    },
    {
      exact: true,
      // Support/devtools (same showDevTools as Internal dashboard) AND
      // enableOrgDesign. Loading grace only for those users so deep links
      // survive flag resolve; customers never see the route.
      isDisabled: !showDevTools || (!enableOrgDesign && !workspaceFlagsLoading),
      isRestricted: true,
      title: "Org Design settings",
      path: PATHS.ORG_DESIGN_SETTINGS,
      Component: OrgDesignSettingsPage,
    },
    {
      exact: true,
      isRestricted: true,
      title: "Tenant Settings",
      path: PATHS.ADMIN_DASHBOARD,
      Component: withAdminAccess(AdminConfiguration, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "Workspace Settings",
      path: PATHS.WORKSPACE_SETTINGS,
      Component: withAdminAccess(WorkspaceSettings, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "BambooHR OAuth",
      path: BAMBOOHR_OAUTH_CALLBACK_PATH,
      Component: withAdminAccess(BambooHROAuthCallbackPage, props),
    },
    {
      exact: true,
      isRestricted: true,
      title: "User Access Report",
      path: PATHS.USER_REPORT,
      Component: withAdminAccess(Auth0UserReport, props),
    },
  ]);
};

export default getRoutes;
